import * as React from 'react';

import { enumCameraStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
  ThemeColor,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';

import { cameraDeleteAtom } from '../../../_graphql/mutationCameraDelete';
import { cameraSetCredentialsAtom } from '../../../_graphql/mutationCameraSetCredentials';
import { cameraUpdateAtom } from '../../../_graphql/mutationCameraUpdate';
import { useCameraEventsSubscription } from '../../../_graphql/subscriptionCameraEvents';
import * as state from '../../../_state/cameraDetail.state';
import {
  $discardCameraBaseline,
  $hasUnsavedCameraChanges,
  $resetCameraFormToCamera,
  CameraFormState,
} from '../../../_state/cameraForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateCameraForm } from '../../../hooks/usePopulateCameraForm';
import { useTranslations } from '../../../i18n/useTranslations';
import type { CameraStatus } from '../../../types/sidecar';

import { CameraHeaderActions } from './CameraHeaderActions';
import { CameraTabDetails } from './CameraTabDetails';
import { CameraTabLiveFeed } from './CameraTabLiveFeed';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<CameraStatus, ThemeColor> = {
  [enumCameraStatus.Discovered]: 'warning',
  [enumCameraStatus.Configured]: 'primary',
};

const PHASE_COLORS: Record<string, ThemeColor> = {
  discovered: 'neutral',
  provisioning: 'warning',
  wrong_credentials: 'failure',
  operational: 'success',
};

type CameraDetailTab = 'details' | 'liveFeed';

export const CameraDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] =
    React.useState<CameraDetailTab>('details');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [sidecarPhase, setSidecarPhase] = React.useState<string | null>(null);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<CameraDetailTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedCameraChanges);
  const [, resetForm] = useAtom($resetCameraFormToCamera);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardCameraBaseline);

  const cameraId = useAtomValue(state.$cameraId);
  const { data: camera, refetch } = useAtomValue(state.$remoteCameraDetail);
  const isLoading = useAtomValue(state.$isCameraLoading);
  const error = useAtomValue(state.$cameraError);

  const deleteCamera = useSetAtom(cameraDeleteAtom({}));
  const updateCamera = useSetAtom(cameraUpdateAtom({}));
  const setCredentials = useSetAtom(cameraSetCredentialsAtom({}));
  const presenceMembers = useHardwarePresenceMembers();
  const toast = useToast();

  const { connection, isConnecting } = useHardwareSidecarConnection({
    sidecar: camera?.sidecarDetail,
  });

  usePopulateCameraForm({ camera });

  useCameraEventsSubscription(undefined, {
    onEvent: (event) => {
      if (event?.id === cameraId) {
        refetch?.();
      }
    },
  });

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.hardwareView.cameraDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: CameraFormState) => {
      if (!cameraId || !data) return;

      try {
        setIsUpdating(true);
        await updateCamera({
          id: cameraId,
          name: data.name.trim(),
          streamProfile: data.streamProfile,
          streamUrl: data.streamUrl.trim() || undefined,
          snapshotUrl: data.snapshotUrl.trim() || undefined,
        });

        const credentialsChanged =
          data.username.trim() !== (camera?.username ?? '') ||
          data.password.trim() !== (camera?.passwordDecrypted ?? '');

        if (
          credentialsChanged &&
          data.username.trim() &&
          data.password.trim()
        ) {
          await setCredentials({
            id: cameraId,
            username: data.username.trim(),
            password: data.password.trim(),
          });
        }

        setDiscardBaseline(data);
        toast.success(t.hardwareView.cameraDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.cameraDetailView.toast.updateError(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      cameraId,
      camera,
      updateCamera,
      setCredentials,
      setDiscardBaseline,
      toast,
      t,
      refetch,
    ],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (currentTab === 'details' && hasUnsavedChanges) {
        setPendingTabChange(newTab as CameraDetailTab);
        return false;
      }
      return true;
    },
    [currentTab, hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!cameraId) return;

    try {
      await deleteCamera({ id: cameraId });
      toast.success(t.hardwareView.cameraDetailView.messages.deleteSuccess);
      setLocation('/hardware');
    } catch (err) {
      toast.error(
        t.hardwareView.cameraDetailView.messages.deleteError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
      throw err;
    }
  }, [cameraId, deleteCamera, toast, setLocation, t]);

  const handlePhaseChange = React.useCallback((phase: string | null) => {
    setSidecarPhase(phase);
  }, []);

  if (isLoading && !camera) {
    return (
      <div className="@CameraDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20" />
            <div className="h-8 w-1/3 rounded bg-neutral/20" />
            <div className="h-32 rounded bg-neutral/20" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !camera) {
    return (
      <div className="@CameraDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.hardwareView.common.errorLabel}:{' '}
          {error?.message ??
            t.hardwareView.cameraDetailView.messages.cameraNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.cameraDetailView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  return (
    <div className="@CameraDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as CameraDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={camera.name}
        subtitle={
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              {sidecarPhase &&
              sidecarPhase in t.hardwareView.camerasPanel.phaseLabels ? (
                <Badge color={PHASE_COLORS[sidecarPhase] ?? 'neutral'}>
                  {
                    t.hardwareView.camerasPanel.phaseLabels[
                      sidecarPhase as keyof typeof t.hardwareView.camerasPanel.phaseLabels
                    ]
                  }
                </Badge>
              ) : (
                <Badge color={STATUS_COLORS[camera.status]}>
                  {t.hardwareView.camerasPanel.statusLabels[camera.status]}
                </Badge>
              )}
              <Badge
                color={presenceMembers.has(camera.id) ? 'success' : 'neutral'}
              >
                {presenceMembers.has(camera.id)
                  ? t.hardwareView.cameraDetailView.presenceLabels.online
                  : t.hardwareView.cameraDetailView.presenceLabels.offline}
              </Badge>
              {isConnecting && (
                <Badge color="warning">
                  {t.hardwareView.cameraDetailView.connectionLabels.connecting}
                </Badge>
              )}
              {connection?.connected && (
                <Badge
                  color={connection.type === 'direct' ? 'primary' : 'neutral'}
                >
                  {connection.type === 'direct'
                    ? t.hardwareView.cameraDetailView.connectionLabels.direct
                    : t.hardwareView.cameraDetailView.connectionLabels.relayed}
                </Badge>
              )}
              {!connection?.connected && !isConnecting && (
                <Badge color="warning">
                  {
                    t.hardwareView.cameraDetailView.connectionLabels
                      .disconnected
                  }
                </Badge>
              )}
              {camera.driverType && (
                <span className="text-neutral/60">{camera.driverType}</span>
              )}
              <span className="text-neutral/60">
                {'·'} {t.hardwareView.cameraDetailView.fields.created}{' '}
                <LocalizedDate date={camera.timestamps.createdAt} />
              </span>
            </div>
            {sidecarPhase === 'wrong_credentials' && (
              <div className="text-sm text-failure">
                {
                  t.hardwareView.cameraDetailView.messages
                    .wrongCredentialsWarning
                }
              </div>
            )}
          </div>
        }
        headerActions={
          <CameraHeaderActions
            camera={camera}
            onDelete={handleDelete}
            onPhaseChange={handlePhaseChange}
            refetch={refetch}
          />
        }
        tabs={{
          details: {
            title: t.hardwareView.cameraDetailView.tabs.details,
            content: () => (
              <CameraTabDetails
                camera={camera}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          liveFeed: {
            title: t.hardwareView.cameraDetailView.tabs.liveFeed,
            content: () => <CameraTabLiveFeed camera={camera} />,
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.hardwareView.cameraDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
